import React, { useState } from 'react';
import { t } from 'i18next';
import {
  AppShell, Button, TOPBAR_HEIGHT, useAutoHide, useMediaQuery, usePersistedState,
  type TopbarItem,
} from '@datanomik/core-ui';
import { MenuLine } from '@datanomik/core-ui/icons';

import { SidebarChrome } from './chrome/SidebarChrome';
import { TopbarChrome } from './chrome/TopbarChrome';
import { MobileNavSheet } from './chrome/MobileNavSheet';
import { ShellPageBar } from './page/ShellPageBar';
import { ShellScrollProvider } from './ShellScrollContext';
import type { ShellNavSection } from './nav/types';

export type ShellChrome = 'sidebar' | 'topbar';

export const SIDEBAR_PINNED_KEY = 'dn_sidebar_pinned';

export interface AppChromeProps {
  chrome: ShellChrome;
  sections: ShellNavSection[];
  topbarItems?: TopbarItem[];
  pageTitle?: React.ReactNode;
  pageIsBeta?: boolean;

  brand?: React.ReactNode;
  brandCompact?: React.ReactNode;
  account?: React.ReactNode;
  accountCompact?: React.ReactNode;
  footerExtras?: React.ReactNode;
  headerActions?: React.ReactNode;
  pageBarActions?: React.ReactNode;
  pageBarLeading?: React.ReactNode;

  onSearch?: () => void;
  loading?: boolean;
  overlays?: React.ReactNode;
  navHidden?: boolean;
  children?: React.ReactNode;
}

export const AppChrome: React.FC<AppChromeProps> = ({
  chrome,
  sections,
  topbarItems = [],
  pageTitle,
  pageIsBeta,
  brand,
  brandCompact,
  account,
  accountCompact,
  footerExtras,
  headerActions,
  pageBarActions,
  pageBarLeading,
  onSearch,
  loading,
  overlays,
  navHidden,
  children,
}) => {
  const isMobile = useMediaQuery('(max-width: 767px)');
  const [pinned, setPinned] = usePersistedState<boolean>(SIDEBAR_PINNED_KEY, false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const useTopbar = chrome === 'topbar' && !isMobile && !navHidden;
  const useSidebar = chrome === 'sidebar' && !isMobile && !navHidden;

  const { hidden: headerHidden, scrolled } = useAutoHide({
    threshold: TOPBAR_HEIGHT,
    enabled: useTopbar,
  });

  const mobileTrigger = isMobile && !navHidden ? (
    <Button
      variant="ghost"
      size="icon"
      aria-label={t('Open menu')}
      onClick={() => setMobileNavOpen(true)}
      className="text-muted-foreground hover:text-foreground -ml-2"
    >
      <MenuLine className="size-5" />
    </Button>
  ) : undefined;

  const leading = mobileTrigger || pageBarLeading ? (
    <>
      {mobileTrigger}
      {pageBarLeading}
    </>
  ) : undefined;

  return (
    <ShellScrollProvider hidden={useTopbar && headerHidden} scrolled={scrolled}>
      <AppShell
        data-modern
        data-chrome={chrome}
        loading={loading}
        sidebarExpanded={pinned}
        sidebar={useSidebar ? (
          <SidebarChrome
            expanded={pinned}
            onToggleExpanded={() => setPinned((p) => !p)}
            sections={sections}
            brand={brand}
            account={account}
            footerExtras={footerExtras}
            onSearch={onSearch}
          />
        ) : undefined}
        header={useTopbar ? (
          <TopbarChrome
            items={topbarItems}
            brand={brandCompact}
            actions={headerActions}
            hidden={headerHidden}
            onSearch={onSearch}
          />
        ) : undefined}
        pageBar={(
          <ShellPageBar
            title={pageTitle}
            isBeta={pageIsBeta}
            stickyTop={useTopbar ? TOPBAR_HEIGHT : 0}
            hidden={useTopbar && headerHidden}
            leading={leading}
            globalActions={isMobile && !navHidden ? pageBarActions : undefined}
          />
        )}
        overlays={overlays}
      >
        {children}
      </AppShell>

      {isMobile && !navHidden ? (
        <MobileNavSheet
          open={mobileNavOpen}
          onClose={() => setMobileNavOpen(false)}
          sections={sections}
          brand={brand}
          account={accountCompact ?? account}
        />
      ) : null}
    </ShellScrollProvider>
  );
};

export default AppChrome;
